import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import { useAuth } from "../../../context/auth-context";
import { formatDate, formatToZMW, handlePrint, rootUrl } from "../../../helpers";
import DataTable from "react-data-table-component";
import { FaEye, FaFilePdf, FaPlus, FaPrint, FaSpinner, FaTimes, FaTrash, FaUpload } from "react-icons/fa";
import SupplierStatement from "./SupplierStatement";
import { useOutletContext } from 'react-router-dom';
import AddBillModal from "../bills/addBill";
import { toast } from "react-toastify";
import { MdPayments } from "react-icons/md";
import { customStyles } from "../../../styles";
import PaymentVoucher from "../../../components/PaymentVoucher";
import FormGenerator from "../../../components/formGenerator";
import { BiTransfer } from "react-icons/bi";

const SupplierManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [transferFields, setTransferFields] = useState([]);
  const [title, setTitle] = useState('');
  const [output, setOutput] = useState('');
  const [formType, setFormType] = useState(1); // 1: Invoice, 2: Payment
   const [transactions, setTransactions] = useState([]);
      const [filteredCashbooks, setFilteredCashbooks] = useState([]);
        const [filters, setFilters] = useState({
          startDate: '',
          endDate: '',
          bank: '',
          customer: '',
          search: '',
        });
  const [formData, setFormData] = useState({
    name: "",
    contactPerson: "",
    phoneNumber: "",
    email: "",
    address: "",
    type: "",
  });

  const {allData, setAllData} = useOutletContext();
  const auth = useAuth();
 const componentRef = useRef(null);
  const [isSlidingDivOpen, setIsSlidingDivOpen] = useState(false);
    const [showSlidingDiv, setShowSlidingDiv] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [attachmentList, setAttachmentList] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const openSlidingDiv = () => {
    setShowSlidingDiv(true);
  };
  const closeSlidingDiv = () => {
    setShowSlidingDiv(false);
  };
  const toggleSlidingDiv = () => setIsSlidingDivOpen(!isSlidingDivOpen);


    const fetchPayments = () => {
      axios.get(rootUrl(`/getPayments/${auth.user.id}`))
        .then((response) => setTransactions(response.data.data))
        .catch((error) => console.error("Error fetching suppliers:", error));
    };

    useEffect(() => {
      fetchPayments();
    }, []);

    useEffect(() => {
      if (allData.bank !== undefined) {
        setTransferFields([
          {
            type: "select",
            name: "fromBank",
            label: "From",
            required: true,
            options: allData.bank.map((res) => ({
              value: res.id,
              label: res.name,
            })),
          },
          {
            type: "select",
            name: "toBank",
            label: "To",
            required: true,
            options: allData.bank.map((res) => ({
              value: res.id,
              label: res.name,
            })),
          },
          { type: "text", name: "amount", label: "Amount", required: true },
          { type: "date", name: "date", label: "Date", required: true },
          { type: "textarea", name: "description", label: "Description" },
        ]);
      }
    }, [allData]);

    const onTransferSubmit = async (formdata) => {
      formdata["uid"] = auth.user.id;
      try {
        const response = await axios.post(rootUrl(`/transfer`), formdata);
        if (response.status === 200) {
          toast.success(response?.data?.msg || "Transfer submitted successfully");
          setIsTransferModalVisible(false);
          fetchPayments();
        }
      } catch (error) {
        toast.error(error?.response?.data?.msg || error?.message);
      }
    };


    const display = (obj) => {
      openSlidingDiv();
      setTitle("Payment voucher");
      setOutput(<PaymentVoucher data={obj} ref={componentRef} />);
    };

    const displayWithAttachments = async (row) => {
      try {
        const res = await axios.get(
          rootUrl(`/getVoucherAttachments/${row.id}`)
        );
        const list = res.data?.data || [];
        openSlidingDiv();
        setTitle("Payment voucher & attached PDFs");
        setOutput(
          <VoucherWithAttachments row={row} attachments={list} printRef={componentRef} />
        );
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load attachments");
      }
    };

    const openUploadModal = async (row) => {
      setUploadTarget(row);
      try {
        const res = await axios.get(
          rootUrl(`/getVoucherAttachments/${row.id}`)
        );
        setAttachmentList(res.data?.data || []);
      } catch {
        setAttachmentList([]);
      }
    };

    const closeUploadModal = () => {
      setUploadTarget(null);
      setAttachmentList([]);
    };

    const handlePdfUpload = async (e) => {
      const files = e.target.files;
      if (!files?.length || !uploadTarget) return;
      setUploadingFiles(true);
      try {
        for (let i = 0; i < files.length; i++) {
          const fd = new FormData();
          fd.append("file", files[i]);
          fd.append("cash_id", uploadTarget.id);
          fd.append("uid", auth.user.id);
          const r = await axios.post(rootUrl("/uploadVoucherAttachment"), fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (r.data?.status !== 200) {
            toast.error(r.data?.message || "Upload failed");
            break;
          }
        }
        toast.success("File(s) uploaded");
        const res = await axios.get(
          rootUrl(`/getVoucherAttachments/${uploadTarget.id}`)
        );
        setAttachmentList(res.data?.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Upload failed");
      } finally {
        setUploadingFiles(false);
        e.target.value = "";
      }
    };

    const removeAttachment = async (att) => {
      if (!window.confirm("Delete this PDF attachment?")) return;
      try {
        await axios.post(rootUrl("/deleteVoucherAttachment"), {
          id: att.id,
          uid: auth.user.id,
        });
        toast.success("Removed");
        setAttachmentList((prev) => prev.filter((a) => a.id !== att.id));
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Delete failed");
      }
    };
  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle modal open/close
  const toggleModal = () => setIsModalOpen((prev) => !prev);

  function isAnyValueEmpty(obj) {
    return Object.values(obj).some(value => 
        value === null || value === undefined || value === ''
    );
}



  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    
    
    formData['uid'] = auth.user.id
    try{
       if(isAnyValueEmpty(formData)){
            toast.error("Please ensure to fill in all fields")
            return
          }
          // formData['type'] = 0;
      const response = await axios.post(rootUrl("/addSupplier"), formData)
      if(response.data.status === 200){
        toast.success("Submitted successfully")
        toggleModal();
        setFormData(
          {
            name: "",
            contactPerson: "",
            phoneNumber: "",
            email: "",
            address: "",
            
          }
        )

        axios.get(rootUrl(`/getSuppliers/${auth.user.id}`)) 
      .then((response) => setAllData((prevData) => ({
        ...prevData, 
        suppliers: response.data.data,
      })))
      .catch((error) => console.error("Error fetching suppliers:", error));

      
      }else{
        toast.info(response.data.msg)
      }

    }catch(e){
      toast.error(e?.message)
    }

  };

  

  function viewSupp(id, type, name){

    

    openSlidingDiv()

    if(type === 1){
      setTitle('Supplier Statement')
      setOutput(<SupplierStatement id={id} name={name} />)
    }

    if(type === 2){
      setTitle('Add Payment')
      setOutput(<AddBillModal data={allData} onSubmit={onSubmit} type={2} />)
    }
  }


  
  const onSubmit = async (payload)=>{
    payload['uid'] = auth.user.id

    try{
        const response = await axios.post(rootUrl('/paySupplier'), payload)
        if(response.data.status === 200){
            toast.success("Payment submitted for approval")
            closeSlidingDiv()
            axios.get(rootUrl(`/getPayments/${auth.user.id}`)) 
      .then((response) => setTransactions(response.data.data))
      .catch((error) => console.error("Error fetching suppliers:", error));
  
        }else{
            toast.error("Something went wrong")
        }
    }catch(e){
        toast.error(e?.message)
    }
}


  const columns = [
     {
       name: 'Sn',
       selector: (row, index) => index + 1,
       sortable: true,
       width: '50px',
     },
     {
       name: 'Date',
       selector: (row) => formatDate(row.date.substring(0, 10)),
       sortable: true,
     },
     {
       name: 'Chart of Account',
       selector: (row) =>
         allData?.budget?.find((a) => a.id == row.coa_id)?.coa || '',
       sortable: true,
     },
     {
       name: 'Bank',
       selector: (row) =>
         allData?.bank?.find((a) => a.id === row.bank)?.name || '',
       sortable: true,
     },
     {
       name: 'Description',
       selector: (row) => row.description || '',
       sortable: true,
     },
     {
       name: 'Voucher #',
       selector: (row) => row.voucher || '',
       sortable: true,
     },{
       name: 'Cheque #',
       selector: (row) => row.cheque || '',
       sortable: true,
     },
     {
       name: 'Payee',
       selector: (row) => allData?.suppliers?.find((a) => a.id == row.paye)?.name || ''
         
       ,
       sortable: true,
     },
     {
       name: 'Project',
       selector: (row) => allData?.projects?.find((a) => a.id == row.proj_id)?.name || ''
       ,
       sortable: true,
     },
     {
       name: 'Debit',
       selector: (row) => (row.type === 'income' ? formatToZMW(row.amount) : 0),
       sortable: true,
     },
     {
       name: 'Credit',
       selector: (row) => (row.type === 'expense' ? formatToZMW(row.amount) : 0),
       sortable: true,
     },
     {
       name: 'Balance',
       selector: (row) => formatToZMW(row.balance || 0),
       sortable: true,
     },
     {
       name: 'Actions',
       cell: (row) => (
         <div className="flex flex-wrap items-center gap-1">
           <button
             type="button"
             className="bg-blue-950 hover:bg-blue-900 text-white px-2 py-1.5 rounded text-xs inline-flex items-center justify-center"
             title="View payment voucher only"
             onClick={() => display(row)}
           >
             <FaEye />
           </button>
           <button
             type="button"
             className="bg-slate-700 hover:bg-slate-800 text-white px-2 py-1.5 rounded text-xs inline-flex items-center justify-center"
             title="View voucher with all attached PDFs"
             onClick={() => displayWithAttachments(row)}
           >
             <FaFilePdf />
           </button>
           <button
             type="button"
             className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 py-1.5 rounded text-xs inline-flex items-center justify-center"
             title="Upload or manage PDF attachments"
             onClick={() => openUploadModal(row)}
           >
             <FaUpload />
           </button>
         </div>
       ),
       ignoreRowClick: true,
       allowOverflow: true,
       button: true,
     },
   ];
  
   useEffect(() => {
     let filtered = transactions;
 
     if (filters.startDate) {
       filtered = filtered.filter((row) => {
         const rowDate = new Date(row.date);
         const startDate = new Date(filters.startDate);
       
         // Set the time part to midnight (00:00:00) for both dates
         rowDate.setHours(0, 0, 0, 0);
         startDate.setHours(0, 0, 0, 0);
       
         return rowDate >= startDate;
       });
       
       
     }
     if (filters.endDate) {
       filtered = filtered.filter((row) => {
         const rowDate = new Date(row.date);
         const endDate = new Date(filters.endDate);
       
         // Set the time part to midnight (00:00:00) for both dates
         rowDate.setHours(0, 0, 0, 0);
         endDate.setHours(0, 0, 0, 0);
       
         return rowDate <= endDate;
       });
       
       
     }
     if (filters.bank) {
       filtered = filtered.filter((row) => row.bank == filters.bank);
     }
     if (filters.customer) {
      filtered = filtered.filter((row) => row.paye == filters.customer);
    }
     if (filters.search) {
       filtered = filtered.filter(
         (row) =>
           row.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
         row.voucher?.toLowerCase().includes(filters.search.toLowerCase()) ||
         row.cheque?.toLowerCase().includes(filters.search.toLowerCase()) 
       );
     }
     setFilteredCashbooks(filtered);
   }, [filters, transactions]);
   
   const finalBalance = filteredCashbooks.reduce((total, row) => {
     const amount = parseFloat(row.amount) || 0; 
     return row.type === 'income' ? total + amount : total - amount;
   }, 0);
   const calculateBalance = () => {
     let balance = 0;
     return filteredCashbooks.map((row) => {
       balance += row.type === 'income' ? parseFloat(row.amount) : - parseFloat(row.amount);
       return { ...row, balance };
     });
   };

   const customerOptions = [...new Set(transactions.map(res => res.paye))].map((cust) => {
    const supplier = allData?.suppliers.find(a => a.id == cust);
    return {
      value: cust,
      label: supplier?.name || cust,
    };
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <MdPayments className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Payments</h1>
              <p className="text-blue-100 text-sm mt-1">
                Manage supplier and employee payments linked to your cashbook.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsTransferModalVisible(true)}
              className="inline-flex items-center bg-white/20 hover:bg-white/30 text-white h-9 px-3 rounded-lg text-xs md:text-sm font-medium transition-colors border border-white/30"
            >
              <BiTransfer className="mr-1" /> Transfer
            </button>
            <button
              onClick={() => viewSupp(1, 2, "")}
              className="inline-flex items-center bg-white/20 hover:bg-white/30 text-white h-9 px-3 rounded-lg text-xs md:text-sm font-medium transition-colors border border-white/30"
            >
              <FaPlus className="mr-1" /> Add Payment
            </button>
            <button
              onClick={toggleModal}
              className="inline-flex items-center bg-white/20 hover:bg-white/30 text-white h-9 px-3 rounded-lg text-xs md:text-sm font-medium transition-colors border border-white/30"
            >
              <FaPlus className="mr-1" />
              Add Payee
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full flex flex-col bg-white min-h-[400px] rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100">

        {/* Supplier Table */}
        <div id="supTable">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <input
            type="date"
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
          <input
            type="date"
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
          <select
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.bank}
            onChange={(e) => setFilters({ ...filters, bank: e.target.value })}
          >
            <option value="">All CashBooks</option>
            {allData?.bank?.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </select>

          <Select
            options={customerOptions}
            placeholder="Payee"
            isClearable
            value={customerOptions.find(option => option.value === filters.customer)}
            onChange={(selectedOption) =>
              setFilters({ ...filters, customer: selectedOption ? selectedOption.value : "" })
            }
            className="text-sm"
          />
          <input
            type="text"
            placeholder="Search by description, voucher or cheque..."
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <div className="flex items-center text-xs md:text-sm text-gray-600">
            <span className="font-medium mr-1">Showing:</span>
            {filteredCashbooks.length} record{filteredCashbooks.length === 1 ? "" : "s"}
          </div>
        </div>

          <p className="text-xs text-gray-500 mb-2">
            A list of all payments made to suppliers and employees.
          </p>

          <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <DataTable
              title=""
              columns={columns}
              data={calculateBalance()}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </div>
        </div>

          <div className="text-right font-semibold mt-3 text-sm md:text-base text-gray-800">
            Balance:{" "}
            <span className="font-bold text-blue-950">
              {formatToZMW(finalBalance)}
            </span>
          </div>
        </div>

        {/* Sliding voucher/statement panel */}
        {showSlidingDiv && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="flex-1 bg-black/40"
            onClick={closeSlidingDiv}
          />
          <div
            id="slidingDiv"
            className="bg-white h-full w-full sm:w-4/5 lg:w-2/3 xl:w-1/2 shadow-2xl relative flex flex-col"
          >
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-950 to-blue-800 px-6 py-4 text-white p-3 border-b border-gray-200">
              <h3 className="font-semibold text-sm md:text-lg text-white">
                {title}
              </h3>
              <button
                onClick={closeSlidingDiv}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 p-3 border-b border-gray-100">
              <button
                onClick={() => handlePrint(componentRef)}
                className="inline-flex items-center bg-blue-950 text-white px-3 py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-900"
              >
                <FaPrint className="mr-1" />
                Print
              </button>
              {title?.includes("attached") && (
                <span className="text-xs text-gray-500 self-center">
                  Printing includes voucher and embedded PDFs below.
                </span>
              )}
            </div>
            <div className="flex-1 overflow-auto p-3 md:p-4" id="form">
              {output}
            </div>
          </div>
        </div>
      )}

        {/* Add/Edit Payee Modal */}
        {isTransferModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm md:text-base font-semibold text-gray-800">
                  New bank transfer
                </h3>
                <button
                  onClick={() => setIsTransferModalVisible(false)}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  <FaTimes size={14} />
                </button>
              </div>
              <div className="px-4 py-4">
                <FormGenerator fields={transferFields} onSubmit={onTransferSubmit} />
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Payee Modal */}
        {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-4 md:p-5 w-full max-w-xl shadow-xl">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-3 border-b pb-3">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                {selectedSupplier ? "Edit Supplier" : "Add New Payee"}
              </h2>
              <button
                onClick={toggleModal}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                <FaTimes size={14} />
              </button>
            </div>
            {/* Modal Body */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select 
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.type}
                onChange={handleInputChange}
                name="type"
              >
                <option value="">Select Type</option>
                <option value="1">Supplier</option>
                <option value="2">Employee</option>
              </select>
              <input
                name="name"
                placeholder={parseInt(formData.type) === 2 ? 'Employee Name' : 'Supplier Name'}
                value={formData.name}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                name="contactPerson"
                placeholder={parseInt(formData.type) === 2 ? 'Employee Man No' : 'Contact Person'}
                value={formData.contactPerson}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                name="phoneNumber"
                placeholder="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                name="address"
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {/* Other fields based on form type */}
              {formType === 2 && (
                <>
                  <Select
                    name="budget"
                    placeholder="Select Budget"
                    options={[]} // Populate options dynamically
                    onChange={(option) =>
                      setFormData((prev) => ({ ...prev, budget: option.value }))
                    }
                    className="text-sm"
                  />
                  <textarea
                    name="description"
                    placeholder="Description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2"
                  />
                </>
              )}
            </div>
            {/* Modal Footer */}
            <div className="flex justify-end mt-4 gap-2">
              <button
                onClick={toggleModal}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={handleSubmit}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

        {uploadTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">PDF attachments</h3>
                  <p className="text-xs text-gray-500">Voucher {uploadTarget.voucher || uploadTarget.cheque}</p>
                </div>
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                <label className="flex flex-col gap-2 border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-emerald-500 cursor-pointer transition-colors">
                  <span className="text-sm font-medium text-gray-700">
                    Add PDFs (multiple allowed)
                  </span>
                  <span className="text-xs text-gray-500">Only .pdf files</span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    disabled={uploadingFiles}
                    onChange={handlePdfUpload}
                  />
                  {uploadingFiles ? (
                    <span className="inline-flex items-center gap-2 text-sm text-emerald-700">
                      <FaSpinner className="animate-spin" /> Uploading…
                    </span>
                  ) : (
                    <span className="text-sm text-emerald-700">
                      Click to choose files or drag & drop (browser dependent)
                    </span>
                  )}
                </label>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">Attached files</p>
                  {attachmentList.length === 0 ? (
                    <p className="text-sm text-gray-500">No PDFs yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {attachmentList.map((a) => (
                        <li
                          key={a.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                        >
                          <a
                            href={rootUrl(`/uploads/${a.filename}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-800 hover:underline truncate flex-1"
                            title={a.original_filename}
                          >
                            {a.original_filename || a.filename}
                          </a>
                          <button
                            type="button"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                            onClick={() => removeAttachment(a)}
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function VoucherWithAttachments({ row, attachments, printRef }) {
  return (
    <div ref={printRef} className="space-y-8">
      <PaymentVoucher data={row} />
      {attachments?.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">Attached PDFs</p>
          {attachments.map((a) => (
            <div key={a.id} className="mb-6 last:mb-0">
              <p
                className="text-xs text-gray-500 mb-2 truncate"
                title={a.original_filename}
              >
                {a.original_filename || a.filename}
              </p>
              <iframe
                title={a.original_filename || "PDF"}
                src={rootUrl(`/uploads/${a.filename}`)}
                className="w-full min-h-[65vh] border border-gray-200 rounded-lg bg-gray-50"
              />
            </div>
          ))}
        </div>
      )}
      {(!attachments || attachments.length === 0) && (
        <p className="text-sm text-gray-500 italic">No PDF attachments for this voucher.</p>
      )}
    </div>
  );
}

export default SupplierManagement;
